import { aabb2ByPolyLine2s } from "../aabb2";
import type { AABB2 } from "../aabb2Types";
import type { Face } from "../brep";
import { faceOrNullCopy, faceNew } from "../brep";
import { Line2Side } from "../core";
import { intersectPlanePlane } from "../intersect";
import { IntersectionKind } from "../intersectRay2";
import { PlaneSide, planeCopy, planeEval, planeNormal, vec3PlaneSide } from "../plane";
import type { Plane } from "../planeTypes";
import { polyLine2Copy, polyLine2ReverseCopy, polyLine2SetCCW } from "../polyLine2";
import type { PolyLine2 } from "../polyLine2Types";
import { polyLine2New } from "../polyLine2Types";
import { ray2Eval, ray2New, ray2Side } from "../ray2";
import type { Ray2 } from "../ray2Types";
import type { Ray3 } from "../ray3";
import { vec3Dot, vec3Sub } from "../vec3";
import type { Point, Polygon, Segment } from "./dcel";
import { dcelFromPolygons } from "./dcelFromPolygons";
import { setOpByPredicatePolygons } from "./setOps";

const { min, max } = Math;

/**
 * Trim a face by removing all of the parts of it in front of the plane. The "front" of the plane is defined
 * as the side towards which the plane normal points.
 *
 * This algorithm proceeds by converting the face and the clipping plane into a planar 2d problem constituted
 * by a DCEL. The vertices of the DCEL are classified as being on the left, right, or collinear with the plane.
 * This is used to determine what faces of the DCEL should be extracted and then converted into faces.
 */
export function trimFaceWithPlane(face: Face, cutPlane: Plane): { kind: IntersectionKind; faces: Face[] } {
  // the intersection ray is in 3d
  // this function returns null if the planes are parallel
  const ray3 = intersectPlanePlane(face.plane, cutPlane);
  if (!ray3) {
    return trimFaceWithPlaneNoTouch(face, cutPlane);
  }

  // project this ray into the face plane to yield a 2d problem
  const ray2 = ray3InPlane(ray3, face.plane);

  // clip the ray inside of the bounding box of the face
  // returns null if the ray doesn't intersect the bounding box (this implies the face and plane don't intersect)
  const aabb = aabb2ByPolyLine2s(face.loops);
  if (!aabb) {
    return trimFaceWithPlaneNoTouch(face, cutPlane);
  }

  const segment2 = clipRay2InsideAABB2(ray2, aabb);
  if (!segment2) {
    return trimFaceWithPlaneNoTouch(face, cutPlane);
  }

  const CLIP_SEGMENT_ID = 1;

  // form a dcel using the face and segments
  const dcel = dcelFromPolygons({
    polygons: [faceToPolygon(face)],
    segments: [segment2],
    segmentPolygonIds: [CLIP_SEGMENT_ID],
  });

  // classify the vertices by the side they are on

  const vertexClassifications = dcel.vertices.map((v) =>
    ray2Side(ray2.originX, ray2.originY, ray2.dirX, ray2.dirY, v[0], v[1], 1e-6),
  );

  // classify the faces by determining if they have non collinear vertices

  const faceClassifications = dcel.faceHalfEdges.map((face) => {
    for (const halfEdgeIndex of face) {
      const halfEdge = dcel.halfEdges[halfEdgeIndex];
      const startClass = vertexClassifications[halfEdge[0]];

      if (startClass !== Line2Side.COLLINEAR) {
        return startClass;
      }
    }

    return Line2Side.COLLINEAR;
  });

  // combine all of the faces that are classified to the front
  const polys = setOpByPredicatePolygons(dcel, (halfEdgeIndex) => {
    const face = dcel.halfEdgeToFace[halfEdgeIndex];
    const faceClass = faceClassifications[face];

    return faceClass === Line2Side.LEFT && dcel.faceToPolygons[face].has(0);
  });

  return { kind: IntersectionKind.TRANSVERSE, faces: polys.map((poly) => polygonToFace(poly, face.plane)) };
}

function trimFaceWithPlaneNoTouch(face: Face, cutPlane: Plane): { kind: IntersectionKind; faces: Face[] } {
  const [x, y, z] = planeEval(face.plane, face.loops[0][0], face.loops[0][1]);
  const [nx, ny, nz] = planeNormal(cutPlane);
  // determine what side the face is on and include or not based on the class
  if (vec3PlaneSide(x, y, z, cutPlane.originX, cutPlane.originY, cutPlane.originZ, nx, ny, nz) === PlaneSide.FRONT) {
    return { kind: IntersectionKind.TRANSVERSE, faces: [] };
  } else {
    const copy = faceOrNullCopy(face);
    return { kind: IntersectionKind.TRANSVERSE, faces: copy ? [copy] : [] };
  }
}

export function polygonToFaceLoops(polygon: Polygon): PolyLine2[] {
  const loops: PolyLine2[] = [];

  for (const loop of polygon) {
    const loopPoly = polyLine2New(2 * loop.length + 2);
    let count = 0;
    for (let i = loop.length - 1; i >= 0; i--) {
      loopPoly[count++] = loop[i][0];
      loopPoly[count++] = loop[i][1];
    }

    loopPoly[count++] = loop[loop.length - 1][0];
    loopPoly[count++] = loop[loop.length - 1][1];

    loops.push(loopPoly);
  }

  return loops;
}

export function polygonToFace(polygon: Polygon, plane: Plane): Face {
  return faceNew(polygonToFaceLoops(polygon), planeCopy(plane));
}

export function faceToPolygon(face: Face, forceOrientCCW?: boolean): Polygon {
  const polygon: Point[][] = [];

  for (let i = 0; i < face.loops.length; i++) {
    const loop: Point[] = [];
    const exLoop = !forceOrientCCW
      ? polyLine2ReverseCopy(face.loops[i])
      : polyLine2SetCCW(polyLine2Copy(face.loops[i]));

    for (let j = 0; j < exLoop.length - 2; j += 2) {
      loop.push([exLoop[j], exLoop[j + 1]]);
    }
    polygon.push(loop);
  }

  return polygon;
}

export function ray3InPlane(ray3: Ray3, plane: Plane) {
  const {
    originX: ray3OriginX,
    originY: ray3OriginY,
    originZ: ray3OriginZ,
    dirX: ray3DirX,
    dirY: ray3DirY,
    dirZ: ray3DirZ,
  } = ray3;

  const { uX, uY, uZ, vX, vY, vZ } = plane;
  const [dx, dy, dz] = vec3Sub(ray3OriginX, ray3OriginY, ray3OriginZ, plane.originX, plane.originY, plane.originZ);

  const ray2 = ray2New(
    vec3Dot(dx, dy, dz, uX, uY, uZ),
    vec3Dot(dx, dy, dz, vX, vY, vZ),
    vec3Dot(ray3DirX, ray3DirY, ray3DirZ, uX, uY, uZ),
    vec3Dot(ray3DirX, ray3DirY, ray3DirZ, vX, vY, vZ),
  );

  return ray2;
}

export function clipRay2InsideAABB2(ray: Ray2, box: AABB2): Segment | null {
  let tmin: number = -Number.POSITIVE_INFINITY,
    tmax: number = Number.POSITIVE_INFINITY;

  const { originX, originY, dirX, dirY } = ray;

  let t1 = -0;
  let t2 = -0;

  if (ray.dirX !== 0) {
    const dirInvX = 1 / ray.dirX;

    t1 = (box.minX - originX) * dirInvX;
    t2 = (box.maxX - originX) * dirInvX;

    tmin = max(tmin, min(t1, t2));
    tmax = min(tmax, max(t1, t2));
  }

  if (ray.dirY !== 0) {
    const dirInvY = 1 / ray.dirY;

    t1 = (box.minY - originY) * dirInvY;
    t2 = (box.maxY - originY) * dirInvY;

    tmin = max(tmin, min(t1, t2));
    tmax = min(tmax, max(t1, t2));
  }

  if (tmax < tmin) {
    return null;
  }

  const [ax, ay] = ray2Eval(originX, originY, dirX, dirY, tmin);
  const [bx, by] = ray2Eval(originX, originY, dirX, dirY, tmax);

  return [ax, ay, bx, by];
}
